import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api from "../api";
import { fmtFechaEC } from "../utils/fecha";

async function get(path) {
  try { const r = await api.get(path); return r.data; } catch { return null; }
}
function fmtDur(s) {
  const n = parseInt(s)||0;
  if (!n) return "0s";
  if (n < 60) return n+"s";
  return Math.floor(n/60)+"m "+n%60+"s";
}
function fmtDate(d) { return fmtFechaEC(d); }  // calldate viene en UTC → hora Ecuador
function KpiCard({ icon, label, value, unit, sub, subColor, iconBg }) {
  return (
    <div className="nv-kpi">
      <div className="nv-kpi-header">
        <span className="nv-kpi-label">{label}</span>
        <div className="nv-kpi-icon" style={{ background:iconBg||"var(--brand-subtle)", color:"var(--brand)" }}>{icon}</div>
      </div>
      <div className="nv-kpi-value">
        {value ?? <span className="nv-spinner" />}
        {unit && value != null && <span style={{ fontSize:11, fontWeight:400, color:"var(--text-muted)", marginLeft:4 }}>{unit}</span>}
      </div>
      {sub && <div className="nv-kpi-sub" style={{ color:subColor||"var(--text-muted)" }}>{sub}</div>}
    </div>
  );
}

function NodeRow({ name, ip, role, ok }) {
  const bg = ok ? "var(--success)" : "var(--danger)";
  const shadow = ok ? "0 0 6px var(--success)" : "none";
  return (
    <div style={{ display:"flex", alignItems:"center", gap:10, padding:"7px 11px", marginBottom:5, background:"var(--bg-raised)", borderRadius:"var(--r-sm)", border:"1px solid var(--border)" }}>
      <div style={{ width:7, height:7, borderRadius:"50%", flexShrink:0, background:bg, boxShadow:shadow }}/>
      <div style={{ flex:1 }}>
        <div style={{ fontSize:12, fontWeight:500, color:"var(--text-primary)" }}>{name}</div>
        <div style={{ fontSize:10, color:"var(--text-muted)", fontFamily:"var(--font-mono)" }}>{ip}</div>
      </div>
      <div style={{ textAlign:"right" }}>
        <div style={{ fontSize:10, fontWeight:700, color:ok?"var(--success)":"var(--danger)" }}>{ok?"Online":"Offline"}</div>
        <div style={{ fontSize:9, color:"var(--text-muted)" }}>{role}</div>
      </div>
    </div>
  );
}

// ── Carrier status row ────────────────────────────────────────────────────────
function CarrierRow({ name, ip, port, status, flags, group }) {
  const active   = status === "active";
  const probing  = status === "probing";
  const dotColor = active ? "var(--success)" : probing ? "var(--warning)" : "var(--danger)";
  const dotGlow  = active ? "0 0 6px var(--success)" : "none";
  const stLabel  = active ? "ACTIVO" : probing ? "PROBANDO" : "INACTIVO";
  const stColor  = active ? "var(--success)" : probing ? "var(--warning)" : "var(--danger)";
  const stBg     = active ? "var(--success-bg)" : probing ? "var(--warning-bg)" : "var(--danger-bg)";
  return (
    <div style={{
      display:"flex", alignItems:"center", gap:10, padding:"9px 12px",
      marginBottom:6, background:"var(--bg-raised)", borderRadius:"var(--r-sm)",
      border:"1px solid var(--border)", transition:"border-color 0.2s",
    }}>
      <div style={{ width:8, height:8, borderRadius:"50%", flexShrink:0,
        background:dotColor, boxShadow:dotGlow }}/>
      <div style={{ flex:1, minWidth:0 }}>
        <div style={{ fontSize:13, fontWeight:600, color:"var(--text-primary)",
          whiteSpace:"nowrap", overflow:"hidden", textOverflow:"ellipsis" }}>
          {name}
        </div>
        <div style={{ fontSize:10, color:"var(--text-muted)", fontFamily:"var(--font-mono)", marginTop:1 }}>
          {ip}:{port} &nbsp;·&nbsp; FLAGS: {flags||"—"}
        </div>
      </div>
      <span style={{
        fontSize:10, fontWeight:700, padding:"2px 10px",
        borderRadius:20, background:stBg, color:stColor,
        border:`1px solid ${stColor}`, whiteSpace:"nowrap", letterSpacing:"0.06em",
      }}>{stLabel}</span>
    </div>
  );
}

// ── Carriers widget ───────────────────────────────────────────────────────────
function CarriersWidget({ carriers, live }) {
  const navigate = useNavigate();
  // Solo mostrar carriers externos (group 2) o todos si no hay grupos
  const list = carriers || [];
  const activeCount   = list.filter(c => c.status === "active").length;
  const inactiveCount = list.filter(c => c.status === "inactive").length;

  return (
    <div className="nv-card">
      <div className="nv-card-header">
        <span className="nv-card-title">◈ Carriers SIP</span>
        <div style={{ display:"flex", alignItems:"center", gap:8 }}>
          <span style={{
            fontSize:9, fontWeight:700, padding:"1px 7px", borderRadius:10,
            background: live ? "var(--success-bg)" : "var(--warning-bg)",
            color:      live ? "var(--success)"    : "var(--warning)",
            border:`1px solid ${live ? "var(--success)" : "var(--warning)"}`,
            letterSpacing:"0.07em",
          }}>{live ? "● LIVE" : "○ —"}</span>
          <button
            onClick={() => navigate("/carrier-monitor")}
            style={{
              background:"none", border:"none", cursor:"pointer",
              fontSize:10, color:"var(--text-muted)", padding:0,
            }}
            title="Ver monitor completo"
          >
            Ver detalle →
          </button>
        </div>
      </div>

      {/* Summary pills */}
      <div style={{ display:"flex", gap:8, marginBottom:12 }}>
        <div style={{
          flex:1, textAlign:"center", padding:"7px 0",
          background:"var(--success-bg)", borderRadius:"var(--r-sm)",
          border:"1px solid var(--success)",
        }}>
          <div style={{ fontSize:18, fontWeight:700, color:"var(--success)", fontFamily:"var(--font-mono)" }}>
            {activeCount}
          </div>
          <div style={{ fontSize:9, color:"var(--success)", textTransform:"uppercase", letterSpacing:"0.07em" }}>
            Activos
          </div>
        </div>
        <div style={{
          flex:1, textAlign:"center", padding:"7px 0",
          background: inactiveCount > 0 ? "var(--danger-bg)" : "var(--bg-raised)",
          borderRadius:"var(--r-sm)",
          border:`1px solid ${inactiveCount > 0 ? "var(--danger)" : "var(--border)"}`,
        }}>
          <div style={{ fontSize:18, fontWeight:700,
            color: inactiveCount > 0 ? "var(--danger)" : "var(--text-muted)",
            fontFamily:"var(--font-mono)" }}>
            {inactiveCount}
          </div>
          <div style={{ fontSize:9, color: inactiveCount > 0 ? "var(--danger)" : "var(--text-muted)",
            textTransform:"uppercase", letterSpacing:"0.07em" }}>
            Inactivos
          </div>
        </div>
        <div style={{
          flex:1, textAlign:"center", padding:"7px 0",
          background:"var(--bg-raised)", borderRadius:"var(--r-sm)",
          border:"1px solid var(--border)",
        }}>
          <div style={{ fontSize:18, fontWeight:700, color:"var(--text-secondary)", fontFamily:"var(--font-mono)" }}>
            {list.length}
          </div>
          <div style={{ fontSize:9, color:"var(--text-muted)", textTransform:"uppercase", letterSpacing:"0.07em" }}>
            Total
          </div>
        </div>
      </div>

      {/* Carrier rows */}
      {list.length > 0
        ? list.map(c => <CarrierRow key={c.id || c.ip} {...c}/>)
        : (
          <div style={{ textAlign:"center", padding:"18px 0",
            fontSize:11, color:"var(--text-muted)" }}>
            Sin datos del SBC API
          </div>
        )
      }
    </div>
  );
}

const COLORS = ["var(--brand)","var(--success)","var(--info)","var(--warning)","var(--text-secondary)"];

export default function Dashboard() {
  const navigate = useNavigate();
  const [res,setRes]     = useState(null);
  const [dst,setDst]     = useState(null);
  const [cdr,setCdr]     = useState(null);
  const [cl,setCl]       = useState(null);
  const [tr,setTr]       = useState(null);
  const [carriers,setCarriers] = useState([]);
  const [sbcLive,setSbcLive]   = useState(false);
  const [busy,setBusy]   = useState(true);
  const [ts,setTs]       = useState(new Date());
  const [nstat,setNstat]     = useState(null); // /noc/nodes — mapa key→bool (compat)
  const [nodes,setNodes]     = useState(null); // /noc/nodes detail — topología real (nombre/ip/rol+ok)
  const [ndb,setNdb]         = useState(null); // /noc/db
  const [nhealth,setNhealth] = useState(null); // /noc/health
  const [acalls,setAcalls]   = useState(null); // /noc/active-calls — llamadas activas reales
  const [trk,setTrk]         = useState(null); // /noc/trunks — troncales cliente registradas/sin registro

  const load = useCallback(async () => {
    setBusy(true);
    const [a,c,d,e,f,sbc,nn,ndbr,nh,ac,tkr] = await Promise.all([
      get("/metricas/resumen?meses=1"),
      get("/metricas/top-destinos?meses=1&limit=5"),
      get("/cdr?limit=8"),
      get("/clientes?limit=1"),
      get("/trunks"),
      get("/noc/carriers"),
      get("/noc/nodes"),
      get("/noc/db"),
      get("/noc/health"),
      get("/noc/active-calls"),
      get("/noc/trunks"),
    ]);
    setRes(a); setDst(c); setCdr(d); setCl(e); setTr(f);
    setNstat(nn?.nodes||null); setNodes(nn?.detail||null); setNdb(ndbr||null); setNhealth(nh||null);
    setAcalls(ac ?? null); // objeto {active_calls, entrantes, salientes, onnet}
    setTrk(tkr||null);

    // Carriers: estado REAL vía /noc/carriers (panel-api). Arquitectura DIRECTA — los
    // carriers (netvoice.trunks) pelan con el softswitch (Asterisk); el SBC no interviene.
    // status="active" = el carrier responde al SIP OPTIONS probe.
    if (sbc?.status === "online") {
      setCarriers(sbc.carriers || []);
      setSbcLive(true);
    } else {
      setSbcLive(false);
      setCarriers([]);
    }
    setBusy(false); setTs(new Date());
  }, []);

  useEffect(() => {
    load();
    const iv = setInterval(load, 30000);
    return () => clearInterval(iv);
  }, [load]);

  const total = parseInt(res?.total_llamadas)||0;
  const cont  = parseInt(res?.contestadas)||0;
  const mins  = parseFloat(res?.total_minutos)||0;
  const asr   = total>0 ? ((cont/total)*100).toFixed(1) : null;
  const acd   = cont>0  ? Math.round((mins/cont)*60)   : null;
  const tConfig = tr?.data?.filter(t=>t.activo==="yes").length ?? 0;  // trunks habilitados (config)
  // online REAL = carriers que responden segun el SBC monitor (status OK); si el SBC API no esta, fallback a config
  const trunksOnline = sbcLive ? carriers.filter(c => c.status==="OK" || c.status==="active").length : null;

  // Estado REAL de cada nodo (misma fuente que /network: up de Prometheus + /noc/db + /noc/health)
  // Topología + estado vienen del backend (/noc/nodes detail) — sin labels hardcodeados.
  const infraNodes = nodes || [];
  const infraAllOk = infraNodes.length > 0 && infraNodes.every(n => n.ok === true);

  return (
    <div>
      <div className="nv-page-header">
        <div>
          <div className="nv-page-title">Dashboard Operativo</div>
          <div className="nv-page-sub">Actualizado: {ts.toLocaleTimeString("es-EC",{hour12:false})} · auto-refresh 30s</div>
        </div>
        <div className="nv-page-actions">
          <button className="nv-btn nv-btn-secondary nv-btn-sm" onClick={load} disabled={busy}>
            {busy ? <span className="nv-spinner"/> : "↺"} Actualizar
          </button>
          <button className="nv-btn nv-btn-ghost nv-btn-sm" onClick={()=>navigate("/metricas")}>Ver métricas →</button>
        </div>
      </div>

      <div className="nv-kpi-grid">
        <KpiCard icon="☎" label="Llamadas (30d)" value={busy?null:total.toLocaleString()} sub={cont.toLocaleString()+" contestadas"} subColor="var(--success)" iconBg="var(--success-bg)"/>
        <KpiCard icon="⏱" label="Minutos (30d)" value={busy?null:Math.round(mins).toLocaleString()} unit="min" sub={(mins/60).toFixed(1)+" horas"} iconBg="var(--info-bg)"/>
        <KpiCard icon="%" label="ASR" value={busy?null:(asr!=null?asr+"%":"—")} sub="Answer Seizure Ratio" subColor={asr&&parseFloat(asr)>65?"var(--success)":"var(--warning)"} iconBg={asr&&parseFloat(asr)>65?"var(--success-bg)":"var(--warning-bg)"}/>
        <KpiCard icon="⏲" label="ACD" value={busy?null:(acd!=null?acd+"s":"—")} sub="Avg Call Duration" iconBg="var(--brand-subtle)"/>
        <KpiCard icon="⊕" label="Carriers Online" value={busy?null:(sbcLive?String(trunksOnline):(tConfig!=null?String(tConfig):"—"))} sub={tConfig+" configurada"+(tConfig===1?"":"s")+(sbcLive?"":" · SBC offline")} subColor={(sbcLive?trunksOnline:tConfig)>0?"var(--success)":"var(--warning)"} iconBg={(sbcLive?trunksOnline:tConfig)>0?"var(--success-bg)":"var(--warning-bg)"}/>
        <KpiCard icon="◻" label="Clientes" value={busy?null:(cl?.total!=null?cl.total.toLocaleString():"—")} sub="Total clientes" iconBg="var(--brand-subtle)"/>
        <KpiCard icon="◎" label="Troncales registradas" value={busy?null:(trk?String(trk.registered):"—")} sub={trk?(trk.assigned+" con DID · "+trk.unregistered+" sin registro"):"Troncales de cliente"} subColor={trk?.registered>0?"var(--success)":"var(--warning)"} iconBg="var(--info-bg)"/>
        <KpiCard icon="▦" label="Llamadas activas" value={busy?null:(acalls?.active_calls!=null?String(acalls.active_calls):"—")} sub={acalls?.entrantes!=null?(`↓ ${acalls.entrantes} entr · ↑ ${acalls.salientes} sal`+(acalls.onnet>0?` · ${acalls.onnet} on-net`:"")):"En este momento"} subColor={acalls?.active_calls>0?"var(--success)":undefined} iconBg="var(--bg-raised)"/>
      </div>

      <div className="nv-grid-2" style={{ marginBottom:14 }}>
        {/* Carriers SIP — reemplaza Tráfico 6 meses */}
        <CarriersWidget carriers={carriers} live={sbcLive}/>

        <div className="nv-card">
          <div className="nv-card-header">
            <span className="nv-card-title">◉ Infraestructura</span>
            <span className={"nv-badge "+(infraAllOk?"nv-badge-ok":"nv-badge-err")}><span className="dot"/>{infraAllOk?"Todos operativos":"Revisar nodos"}</span>
          </div>
          {infraNodes.length === 0
            ? <div style={{ padding:'16px 0', textAlign:'center', color:'var(--text-muted)', fontSize:12 }}>Cargando estado de nodos…</div>
            : infraNodes.map(n=><NodeRow key={n.key} name={n.name} ip={n.ip} role={n.role} ok={n.ok}/>)}
        </div>
      </div>

      <div className="nv-grid-2" style={{ marginBottom:14 }}>
        <div className="nv-card">
          <div className="nv-card-header">
            <span className="nv-card-title">▤ Top destinos (30d)</span>
            <span style={{ fontSize:10, color:"var(--text-muted)" }}>Por minutos</span>
          </div>
          {dst?.data?.length>0 ? dst.data.map((d,i)=>{
            const maxVal = parseFloat(dst.data[0]?.minutos)||1;
            const pct = Math.min(100,(parseFloat(d.minutos)||0)/maxVal*100);
            return (
              <div key={d.numero||i} style={{ marginBottom:9 }}>
                <div style={{ display:"flex", justifyContent:"space-between", marginBottom:3, fontSize:11 }}>
                  <span style={{ color:"var(--text-secondary)", fontFamily:"var(--font-mono)" }}>{d.numero||"—"}</span>
                  <span style={{ color:COLORS[i], fontFamily:"var(--font-mono)", fontWeight:600 }}>{Math.round(parseFloat(d.minutos)||0)} min</span>
                </div>
                <div className="nv-progress"><div className="nv-progress-bar" style={{ width:pct+"%", background:COLORS[i] }}/></div>
              </div>
            );
          }) : <div style={{ fontSize:11, color:"var(--text-muted)", textAlign:"center", padding:"22px 0" }}>Sin datos</div>}
        </div>
        <div className="nv-card">
          <div className="nv-card-header"><span className="nv-card-title">◈ Distribución (30d)</span></div>
          {res ? (
            <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:8 }}>
              {[
                {lbl:"Contestadas",  val:res.contestadas,    color:"var(--success)"},
                {lbl:"No contest.",  val:res.no_contestadas, color:"var(--danger)"},
                {lbl:"Salientes",    val:res.salientes,      color:"var(--info)"},
                {lbl:"Entrantes",    val:res.entrantes,      color:"var(--warning)"},
                {lbl:"On-net",       val:res.onnet,          color:"var(--brand)"},
                {lbl:"Bloqueadas",   val:res.bloqueadas,     color:"var(--danger)"},
                {lbl:"Min. totales", val:Math.round(mins),   color:"var(--text-primary)"},
              ].map(({lbl,val,color})=>(
                <div key={lbl} style={{ background:"var(--bg-raised)", borderRadius:"var(--r-sm)", padding:"10px 12px", textAlign:"center" }}>
                  <div style={{ fontFamily:"var(--font-mono)", fontSize:16, fontWeight:700, color }}>{(parseInt(val)||0).toLocaleString()}</div>
                  <div style={{ fontSize:9, textTransform:"uppercase", letterSpacing:".08em", color:"var(--text-muted)", marginTop:2 }}>{lbl}</div>
                </div>
              ))}
            </div>
          ) : <div className="nv-loading"><span className="nv-spinner"/></div>}
        </div>
      </div>

      <div className="nv-card">
        <div className="nv-card-header">
          <span className="nv-card-title">▤ Últimas llamadas</span>
          <button className="nv-btn nv-btn-ghost nv-btn-sm" onClick={()=>navigate("/cdr")}>Ver CDR completo →</button>
        </div>
        {busy ? <div className="nv-loading"><span className="nv-spinner"/></div> : (
          <div className="nv-table-wrap">
            <table className="nv-table">
              <thead><tr>
                <th>Origen</th><th>Destino</th><th>Contexto</th>
                <th>Duración</th><th>Estado</th><th>Fecha</th>
              </tr></thead>
              <tbody>
                {(cdr?.data||[]).slice(0,8).map((r,i)=>{
                  const dispCls = r.disposition==="ANSWERED"?"nv-badge-ok":r.disposition==="BUSY"?"nv-badge-warn":r.disposition==="NO ANSWER"?"nv-badge-muted":"nv-badge-err";
                  const dispLbl = r.disposition==="ANSWERED"?"● Contestada":r.disposition==="NO ANSWER"?"● Sin resp.":r.disposition==="BUSY"?"● Ocupado":r.disposition||"—";
                  return (
                    <tr key={i}>
                      <td className="mono">{r.src||"—"}</td>
                      <td className="mono">{r.dst||"—"}</td>
                      <td><span style={{ fontSize:10, color:"var(--text-muted)" }}>{r.dcontext||"—"}</span></td>
                      <td className="mono">{fmtDur(r.billsec)}</td>
                      <td><span className={"nv-badge "+dispCls}>{dispLbl}</span></td>
                      <td style={{ fontSize:10, color:"var(--text-muted)" }}>{fmtDate(r.calldate)}</td>
                    </tr>
                  );
                })}
                {(!cdr?.data||cdr.data.length===0) && (
                  <tr><td colSpan={6} style={{ textAlign:"center", color:"var(--text-muted)", padding:22 }}>Sin registros</td></tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
